import {
	ApiError,
	type PortalFile,
	type PortalReturnCase,
	type PortalReturnList,
	type PortalReturnTab,
} from "@khazzin/api-client";
import { formatDateTime } from "@khazzin/i18n/dates";
import { StatusChip } from "@khazzin/ui/patterns";
import { EmptyState, ErrorState, KeyValueList, PageHeader } from "@khazzin/ui/patterns/page";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import {
	Button,
	ConfirmDialog,
	Drawer,
	SegmentedControl,
	SkeletonText,
} from "@khazzin/ui/primitives";
import type { Tone } from "@khazzin/ui/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useApi } from "../../api";
import { named } from "../../app/named";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import { ReportProblemButton } from "../tickets/OpenTicketDrawer";

/**
 * Returns (EP10-S14, DA-10-08): the store's return cases in its own four
 * tabs — Requested, In transit, To inspect, Resolved — with their counts, a
 * case's drawer (its lines, the grade inspection gave each, the photos and
 * what was done with the units), and the shipments whose delivery attempts
 * are spent waiting for Approve RTO. A return is requested from the order
 * it is about; the store never moves stock itself.
 */

export const RETURN_TABS: readonly PortalReturnTab[] = [
	"requested",
	"in_transit",
	"to_inspect",
	"resolved",
];

const TAB_TONES: Record<PortalReturnTab, Tone> = {
	requested: "info",
	in_transit: "brand",
	to_inspect: "warning",
	resolved: "success",
};

/** Each tab's field in the counts. */
const COUNT_OF: Record<PortalReturnTab, keyof PortalReturnList["counts"]> = {
	requested: "requested",
	in_transit: "inTransit",
	to_inspect: "toInspect",
	resolved: "resolved",
};

export function returnsKey(storeId: string) {
	return ["portal", storeId, "returns"] as const;
}

export function ReturnsPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const { store, context } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const level = levelOf(store, "returns");
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const [params, setParams] = useSearchParams();
	const raw = params.get("tab");
	const tab: PortalReturnTab = RETURN_TABS.includes(raw as PortalReturnTab)
		? (raw as PortalReturnTab)
		: "requested";
	const opened = params.get("case");
	const [notice, setNotice] = useState<string | null>(null);
	const [approving, setApproving] = useState<{ shipmentId: string; orderNo: string } | null>(null);
	const at = (iso: string) => formatDateTime(iso, { locale, timeZone });
	const enabled = level !== "none" && storeId !== "";

	const summary = useQuery({
		queryKey: [...returnsKey(storeId), "summary"],
		queryFn: ({ signal }) => api.listPortalReturns({ limit: 1, signal }),
		enabled,
	});
	const page = useCursorPagination<PortalReturnCase>(
		(cursor, limit, signal) =>
			api
				.listPortalReturns({ tab, ...(cursor === null ? {} : { cursor }), limit, signal })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...returnsKey(storeId), "list", tab], enabled },
	);

	if (level === "none") {
		return <NotFound />;
	}

	const open = (returnId: string | null) => {
		const next = new URLSearchParams(params);
		if (returnId === null) {
			next.delete("case");
		} else {
			next.set("case", returnId);
		}
		setParams(next, { replace: true });
	};
	// ADJ-M03-4: sending a parcel back is not undone, so it asks first, and
	// the confirmation is busy until the answer lands (one request per press).
	const approve = ({ shipmentId, orderNo }: { shipmentId: string; orderNo: string }) =>
		api
			.requestPortalReturn({ kind: "approve_rto", shipmentId })
			.then(
				() => setNotice(t("portal.returns.rto.approved", { order: orderNo })),
				(error: unknown) =>
					setNotice(error instanceof ApiError ? error.message : t("portal.returns.refused")),
			)
			.finally(() => {
				setApproving(null);
				void queryClient.invalidateQueries({ queryKey: returnsKey(storeId) });
			});

	const counts = summary.data?.counts;
	const awaiting = summary.data?.awaitingRto ?? [];
	const columns: Column<PortalReturnCase>[] = [
		{
			id: "number",
			headerKey: "portal.returns.columns.number",
			mono: true,
			card: "wide",
			cell: (row) => row.number ?? row.returnId,
		},
		{
			id: "order",
			headerKey: "portal.returns.columns.order",
			mono: true,
			cell: (row) => (
				<Link
					to={`/orders/${encodeURIComponent(row.orderId)}`}
					onClick={(event) => event.stopPropagation()}
				>
					{row.orderNo ?? row.orderId}
				</Link>
			),
		},
		{
			id: "customer",
			headerKey: "portal.returns.columns.customer",
			cell: (row) => row.customerName ?? "—",
		},
		{
			id: "reason",
			headerKey: "portal.returns.columns.reason",
			cell: (row) => t(`portal.returns.reason.${row.reasonCode}`),
		},
		{
			id: "stage",
			headerKey: "portal.returns.columns.stage",
			cell: (row) => <ReturnTabChip tab={row.tab} />,
		},
		{
			id: "disposition",
			headerKey: "portal.returns.columns.disposition",
			cell: (row) =>
				row.disposition ? (
					<StatusChip tone="neutral" size="sm">
						{t(`portal.returns.disposition.${row.disposition}`)}
					</StatusChip>
				) : (
					"—"
				),
		},
		{
			id: "items",
			headerKey: "portal.returns.columns.items",
			align: "end",
			cell: (row) => row.itemCount,
		},
		{
			id: "updated",
			headerKey: "portal.returns.columns.updated",
			cell: (row) => at(row.updatedAt),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("portal.returns.title")} subtitle={t("portal.returns.subtitle")} />
			{awaiting.length > 0 && (
				<section
					aria-labelledby="returns-rto"
					className="rounded-md border border-warning-border bg-warning-bg p-4"
				>
					<h2 id="returns-rto" className="mb-1 text-[15px] font-bold text-fg">
						{t("portal.returns.rto.title")}
					</h2>
					<p className="mb-3 text-[12.5px] text-fg-muted">{t("portal.returns.rto.body")}</p>
					<ul className="flex flex-col gap-2">
						{awaiting.map((rto) => {
							const orderNo = rto.orderNo ?? rto.orderId;
							return (
								<li key={rto.shipmentId} className="flex flex-wrap items-center gap-3 text-[13px]">
									<Link className="font-mono" to={`/orders/${encodeURIComponent(rto.orderId)}`}>
										{orderNo}
									</Link>
									<span className="text-fg-muted">
										{t("portal.returns.rto.attempts", { count: rto.attemptCount })}
										{/* The reason in words, not its code (ADJ-M03-4). */}
										{rto.lastFailureReason
											? ` · ${named(t, "delivery.failureReason", rto.lastFailureReason)}`
											: ""}
									</span>
									{level === "write" && (
										<Button
											size="sm"
											variant="primary"
											onClick={() => setApproving({ shipmentId: rto.shipmentId, orderNo })}
										>
											{t("portal.returns.rto.approve")}
										</Button>
									)}
								</li>
							);
						})}
					</ul>
				</section>
			)}
			{notice !== null && (
				<p role="status" className="text-[12.5px] text-fg">
					{notice}
				</p>
			)}
			<div className="overflow-x-auto">
				<SegmentedControl<PortalReturnTab>
					aria-label={t("portal.returns.title")}
					value={tab}
					onChange={(next) => {
						setParams({ tab: next }, { replace: true });
						page.reset();
					}}
					options={RETURN_TABS.map((value) => ({
						value,
						label: t("portal.returns.tab", {
							label: t(`portal.returns.tabs.${value}`),
							count: counts?.[COUNT_OF[value]] ?? 0,
						}),
					}))}
				/>
			</div>
			<DataTable<PortalReturnCase>
				screenKey="portal.returns"
				caption={t("portal.returns.title")}
				t={t}
				columns={columns}
				rowKey={(row) => row.returnId}
				onRowClick={(row) => open(row.returnId)}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				empty={
					<EmptyState
						title={t("portal.returns.empty.title")}
						body={t("portal.returns.empty.body")}
					/>
				}
			/>
			<ConfirmDialog
				open={approving !== null}
				onOpenChange={(next) => !next && setApproving(null)}
				title={t("portal.returns.rto.confirmTitle", { shipment: approving?.orderNo ?? "" })}
				description={t("portal.returns.rto.confirmBody")}
				confirmLabel={t("portal.returns.rto.approve")}
				cancelLabel={t("ui.actions.cancel")}
				onConfirm={() => (approving === null ? undefined : approve(approving))}
			/>
			{opened !== null && (
				<ReturnCaseDrawer
					returnId={opened}
					storeId={storeId}
					timeZone={timeZone}
					onClose={() => open(null)}
				/>
			)}
		</div>
	);
}

export function ReturnTabChip({ tab }: { tab: PortalReturnTab }) {
	const { t } = useLocale();
	return (
		<StatusChip tone={TAB_TONES[tab]} size="sm">
			{t(`portal.returns.tabs.${tab}`)}
		</StatusChip>
	);
}

/** One case: its lines, what inspection found, the photos, and what was done. */
function ReturnCaseDrawer({
	returnId,
	storeId,
	timeZone,
	onClose,
}: {
	returnId: string;
	storeId: string;
	timeZone: string;
	onClose: () => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const detail = useQuery({
		queryKey: [...returnsKey(storeId), "detail", returnId],
		queryFn: ({ signal }) => api.getPortalReturn(returnId, { signal }),
		retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
		// The photos are signed for five minutes.
		staleTime: 4 * 60_000,
	});
	const c = detail.data?.case;
	const at = (iso: string | null | undefined) =>
		iso ? formatDateTime(iso, { locale, timeZone }) : "—";

	return (
		<Drawer
			open
			onOpenChange={(next) => !next && onClose()}
			size="lg"
			title={c ? (c.number ?? c.returnId) : t("portal.returns.drawer.title")}
			closeLabel={t("ui.actions.close")}
			backLabel={t("ui.actions.back")}
			footer={
				c ? (
					<ReportProblemButton source="return" id={c.returnId} label={c.number ?? c.returnId} />
				) : null
			}
		>
			{detail.isError ? (
				<ErrorState error={detail.error} onRetry={() => void detail.refetch()} t={t} compact />
			) : detail.data === undefined || c === undefined ? (
				<SkeletonText lines={5} />
			) : (
				<div className="flex flex-col gap-4">
					<KeyValueList
						rows={[
							{ label: t("portal.returns.columns.stage"), value: <ReturnTabChip tab={c.tab} /> },
							{
								label: t("portal.returns.columns.order"),
								value: (
									<Link to={`/orders/${encodeURIComponent(c.orderId)}`}>
										{c.orderNo ?? c.orderId}
									</Link>
								),
							},
							{ label: t("portal.returns.columns.customer"), value: c.customerName ?? "—" },
							{
								label: t("portal.returns.columns.reason"),
								value: t(`portal.returns.reason.${c.reasonCode}`),
							},
							{
								label: t("portal.returns.drawer.grade"),
								value: detail.data.damageGrade
									? t(`portal.returns.grade.${detail.data.damageGrade}`)
									: "—",
							},
							{ label: t("portal.returns.drawer.received"), value: at(c.receivedAt) },
							{ label: t("portal.returns.drawer.inspected"), value: at(detail.data.inspectedAt) },
							...(detail.data.notes
								? [{ label: t("portal.returns.drawer.notes"), value: detail.data.notes }]
								: []),
						]}
					/>
					<ul className="flex flex-col gap-3" aria-label={t("portal.returns.drawer.lines")}>
						{detail.data.lines.map((line) => (
							<li
								key={line.returnLineId}
								className="rounded-md border border-border p-3 text-[13px]"
							>
								<div className="flex flex-wrap items-baseline justify-between gap-2">
									<span>
										<span className="font-mono">{line.sku ?? line.orderLineId}</span>
										{line.itemName ? ` · ${line.itemName}` : ""}
									</span>
									<span className="text-fg-muted">
										{t("portal.returns.drawer.qty", {
											received: line.qtyReceived,
											expected: line.qtyExpected,
										})}
									</span>
								</div>
								{line.damageGrade && (
									<p className="mt-1 text-fg-muted">
										{t(`portal.returns.grade.${line.damageGrade}`)}
									</p>
								)}
								{line.dispositions.length > 0 && (
									<p className="mt-1">
										{line.dispositions
											.map((d) =>
												t("portal.returns.drawer.done", {
													qty: d.qty,
													disposition: t(`portal.returns.disposition.${d.disposition}`),
												}),
											)
											.join(" · ")}
									</p>
								)}
								<Photos files={line.photos} label={t("portal.returns.drawer.photo")} />
							</li>
						))}
					</ul>
				</div>
			)}
		</Drawer>
	);
}

/** Thumbnails that open the full photo; a file still uploading says so. */
export function Photos({ files, label }: { files: readonly PortalFile[]; label: string }) {
	const { t } = useLocale();
	if (files.length === 0) {
		return null;
	}
	return (
		<div className="mt-2 flex flex-wrap gap-2">
			{files.map((file, index) =>
				file.url ? (
					<a key={file.url} href={file.url} target="_blank" rel="noreferrer">
						<img
							src={file.url}
							alt={`${label} ${index + 1}`}
							className="h-20 w-20 rounded-md border border-border object-cover"
						/>
					</a>
				) : (
					<span
						// biome-ignore lint/suspicious/noArrayIndexKey: an unsigned file has nothing else to key on
						key={index}
						className="flex h-20 w-20 items-center justify-center rounded-md border border-border text-center text-[11px] text-fg-muted"
					>
						{file.pending
							? t("portal.returns.drawer.uploading")
							: t("portal.returns.drawer.unavailable")}
					</span>
				),
			)}
		</div>
	);
}

export default ReturnsPage;
